import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import updateLocale from 'dayjs/plugin/updateLocale'
import calendar from 'dayjs/plugin/calendar'
import isBetween from 'dayjs/plugin/isBetween'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { isNil } from 'lodash-es'
import appProps from '../app-props'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(updateLocale)
dayjs.extend(calendar)
dayjs.extend(isBetween)
dayjs.extend(advancedFormat)

dayjs.updateLocale('en', {
  weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat'],
})

/**
 * Date utilities class
 */
class DateUtils {
  /**
   * Checks if the specified EAD is today.
   * @param {number} ead The ead in epoch time
   * @returns {boolean} If the `ead` is today
   */
  static isEadToday(ead) {
    if (isNil(ead) || !dayjs(ead).isValid() && ead !== -1) throw new Error('method `isEadToday` requires valid epoch timestamp')

    const now = dayjs().startOf('day')
    const then = dayjs(ead).startOf('day')

    // This is the same logic that `formatEad` uses via `dayjs.calendar` to determine `sameDay`
    // so this and TODAY via `formatEad` should always match up.
    return now.diff(then, 'days') === 0
  }

  /**
   * Formats an EAD (Estimated Arrival Date) to the appropriate format specified for EAD's
   * @param {number} ead The ead in epoch time
   */
  static formatEad(ead, options) {
    if (isNil(ead) || !dayjs(ead).isValid()) throw new Error('method `formatEad` requires valid epoch timestamp')

    const showPickupTime = appProps.get('show.pickup.time')
    const isBopus = options && options.isBopus ? options.isBopus : false
    const tz = options && options.timezone ? options.timezone : 'America/Los_Angeles'
    const hasFallbackEad = options && options.hasFallbackEad ? options.hasFallbackEad : false
    const deliveryDate = dayjs(ead).tz(tz)

    if (!deliveryDate.isValid() && ead !== -1) throw new Error('method `formatEad` requires valid epoch timestamp')

    if (ead === -1) {
      return hasFallbackEad ? 'within 7 business days' : 'date temporarily unavailable'
    }

    return deliveryDate.calendar(null, {
      sameDay: () => {
        if (showPickupTime && isBopus) {
          // If it's on the hour the time is displayed as 6pm
          if (deliveryDate.minute() === 0) {
            return deliveryDate.format('[Today <span class="time">(Ready by] ha[)</span>]')
          }
          // If it's not on the hour the time is displayed as 6:30pm
          return deliveryDate.format('[Today <span class="time">(Ready by] h:mma[)</span>]')
        }
        return 'Today'
      },
      nextDay: () => {
        if (showPickupTime && isBopus) {
          // If it's on the hour the time is displayed as 6pm
          if (deliveryDate.minute() === 0) {
            return deliveryDate.format('[Tomorrow <span class="time">(Ready by] ha[)</span>]')
          }
          // If it's not on the hour the time is displayed as 6:30pm
          return deliveryDate.format('[Tomorrow <span class="time">(Ready by] h:mma[)</span>]')
        }
        return 'Tomorrow'
      },
      nextWeek: 'ddd, MMM D',
      lastDay: '[Yesterday]',
      lastWeek: '[Last] ddd',
      sameElse: 'ddd, MMM D',
    })
  }

  /**
   * Formats a future scheduled delivery date to the month, day and year
   * @param {number} scheduledDeliveryDate The scheduledDeliveryDate in epoch time
   */
  static formatFutureScheduledDeliverDate(scheduledDeliveryDate) {
    if (isNil(scheduledDeliveryDate) || !dayjs(scheduledDeliveryDate).isValid()) throw new Error('method `formatFutureScheduledDeliverDate` requires valid epoch timestamp')

    return dayjs(scheduledDeliveryDate).format('MMM DD, YYYY')
  }

  /**
   * Formats a date to the appropriate format specified for cutoff dates
   * @param {number} dateTime A DateTime in epoch time
   */
  static formatCutoffTime(dateTime) {
    if (isNil(dateTime) || !dayjs(dateTime).isValid() && dateTime !== -1) throw new Error('method `formatCutoffTime` requires valid epoch timestamp')

    return dayjs(dateTime).tz('America/Los_Angeles').format('ha z')
  }

  /**
   * Checks if a date is within three months of the future.
   * Disregards the year, only checks future in months.
   * @param {number} dateTime A DateTime in epoch time
   */
  static isDateWithinThreeMonthsFuture(dateTime) {
    if (isNil(dateTime) || !dayjs(dateTime).isValid()) throw new Error('method `isDateWithinThreeMonthsFuture` requires valid epoch timestamp')

    const today = dayjs()
    const threeMonthsFromToday = dayjs().add(3, 'M')
    const dateToCheck = dayjs(dateTime)
    const dateToCheckIsWithinFirstThreeMonthsOfYear = dateToCheck.month() <= 2

    // We dont want to count the year in the check since these dates are recurring.
    // So, we have to manually set the year so it doesnt interfere with the comparison.
    const yearToSet = today.month() > 9 && dateToCheckIsWithinFirstThreeMonthsOfYear ? today.year() + 1 : today.year()
    const newdateToCheck = dateToCheck.set('y', yearToSet)

    return newdateToCheck.isBetween(today, threeMonthsFromToday)
  }
}

export default DateUtils
